import { useCallback, useEffect, useRef, useState } from "react";
import { isPublishedToFeed, warmFeedPosting } from "../firebase/feed";
import { ToastOrigin, useFeedPostContext } from "../contexts/FeedPostContext";

export interface PublishOptions {
  anonymous: boolean;
  /** Overrides the debate on screen, for callers holding an older render. */
  debateId?: string;
  origin?: ToastOrigin | null;
}

export interface FeedPostState {
  posted: boolean;
  /** A post or removal of this debate is running, wherever it was started. */
  busy: boolean;
  publish: (options: PublishOptions) => Promise<boolean>;
  unpublish: () => Promise<boolean>;
  warm: () => void;
}

/** Whether the debate on screen is on the public feed, and how to change that. */
export function useFeedPost(debateId: string | null, enabled: boolean): FeedPostState {
  const { tasks, postedById, run } = useFeedPostContext();
  const [loaded, setLoaded] = useState(false);
  // Ignores answers that arrive after the person moved to another debate.
  const current = useRef(debateId);
  current.current = debateId;

  useEffect(() => {
    setLoaded(false);
    if (!debateId || !enabled) return;
    isPublishedToFeed(debateId).then((on) => {
      if (current.current === debateId) setLoaded(on);
    });
  }, [debateId, enabled]);

  return {
    posted: !!debateId && enabled && (postedById[debateId] ?? loaded),
    busy: tasks.some((t) => t.debateId === debateId && t.status === "working"),
    publish: useCallback(
      async ({ anonymous, debateId: id, origin }: PublishOptions) => {
        const target = id ?? current.current;
        return target ? run("post", target, { anonymous, origin }) : false;
      },
      [run],
    ),
    unpublish: useCallback(
      async () => (current.current ? run("remove", current.current) : false),
      [run],
    ),
    warm: warmFeedPosting,
  };
}
